import React, { useState, useEffect, useMemo } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'
import { SendMessageModal } from '../messaging/SendMessageModal';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface DownlineTreeViewProps {
  userId: number;
  className?: string;
}

interface TreeNode {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  telegram_username?: string;
  phone?: string;
  total_referrals: number;
  usdt_earnings: number;
  share_earnings: number;
  purchased_shares: number;
  total_purchased: number;
  created_at: string;
  is_active: boolean;
  level: number;
  children: TreeNode[];
  parent_id?: number;
  // Commission YOU earned FROM this person
  commission_from_this_user_usdt: number;
  commission_from_this_user_shares: number;
  // User status flags
  has_purchases: boolean;
  is_team_leader: boolean;
  // Total shares from ALL sources (purchases + bonuses + transfers)
  total_shares: number;
  bonus_shares: number;
  transferred_shares: number;
}

interface TreeStats {
  totalNodes: number;
  activeNodes: number;
  totalLevels: number;
  totalUsdtEarnings: number;
  totalShareEarnings: number;
  activeEarners: number;
  pendingUsers: number;
  teamLeaders: number;
}

// Tab type for the 3-view navigation
type TabType = 'active' | 'pending' | 'all';

export const DownlineTreeView: React.FC<DownlineTreeViewProps> = ({ userId: rawUserId, className = '' }) => {
  // CRITICAL FIX: Ensure userId is always a valid number to prevent type mismatch with database IDs
  // Handle various formats: number, "604", "db_604", UUID strings (which should not happen but need graceful handling)
  const parseUserId = (id: number | string): number => {
    if (typeof id === 'number' && !isNaN(id)) return id;
    if (typeof id === 'string') {
      // Handle "db_604" format
      if (id.startsWith('db_')) {
        const numericPart = parseInt(id.replace(/db_/g, ''), 10);
        if (!isNaN(numericPart)) return numericPart;
      }
      // Try direct parse for numeric strings like "604"
      const parsed = parseInt(id, 10);
      if (!isNaN(parsed)) return parsed;
    }
    console.error('🌳 DownlineTreeView: Invalid userId format:', id, 'type:', typeof id);
    return 0; // Return 0 as fallback (will show "No Downline" which is better than crashing)
  };
  const userId = parseUserId(rawUserId);

  console.log('🌳 DownlineTreeView: Raw userId:', rawUserId, 'Parsed userId:', userId);

  const [treeData, setTreeData] = useState<TreeNode | null>(null);
  const [_allTreeData, setAllTreeData] = useState<TreeNode | null>(null); // Unfiltered tree
  const [treeStats, setTreeStats] = useState<TreeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set([userId]));
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);

  // NEW: Tab navigation state
  // v2.9.101: Default to 'all' tab so pending referrals (like users awaiting payment approval) are visible
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [maxDepth, setMaxDepth] = useState(10); // Default to show all levels
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState<TreeNode | null>(null);

  // Shareholder/holdings map for filtering
  const [_shareholdersSet, setShareholdersSet] = useState<Set<number>>(new Set());
  const [_teamLeadersSet, setTeamLeadersSet] = useState<Set<number>>(new Set());

  // v4.3.79: Copy-to-clipboard feedback for contact popup
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    loadDownlineData();
  }, [userId, maxDepth]);



  const loadDownlineData = async () => {
    setLoading(true);
    try {
      console.log('🌳 DownlineTreeView: Loading data for userId:', userId, 'type:', typeof userId);

      // Fetch all downline data via API
      const response = await fetch(`${API_BASE_URL}/api/user/referral-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'get_downline_tree',
          data: { maxDepth }
        })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load downline data');
      }

      const {
        downline: downlineData,
        commissionTransactions,
        multiLevelCommissions,
        allCommissionTransactions,
        shareBalances,
        shareHoldings,
        commissionBalances,
        commissionConversions
      } = result.data;

      console.log('🌳 DownlineTreeView: Loaded', downlineData?.length, 'users in downline via API');

      // SAFETY: Cap downline size to prevent browser OOM
      if (downlineData && downlineData.length > 500) {
        console.warn('🌳 DownlineTreeView: Downline too large (' + downlineData.length + ' users), truncating to 500');
        downlineData.length = 500;
      }

      // Convert RPC result to user format expected by the rest of the component
      const allUsers = downlineData?.map((row: {
        user_id: number;
        username: string;
        first_name: string;
        last_name: string;
        email: string;
        telegram_username: string;
        phone?: string | null;
        created_at: string;
        is_active: boolean;
      }) => ({
        id: row.user_id,
        username: row.username,
        first_name: row.first_name,
        last_name: row.last_name,
        email: row.email,
        telegram_username: row.telegram_username,
        phone: row.phone || undefined,
        created_at: row.created_at,
        is_active: row.is_active
      })) || [];

      // Build referrals array from the downline data (referrer_id is included)
      const referrals = downlineData?.filter((row: { referrer_id: number | null }) => row.referrer_id !== null)
        .map((row: { referrer_id: number; user_id: number }) => ({
          referrer_id: row.referrer_id,
          referred_id: row.user_id
        })) || [];

      console.log('🌳 DownlineTreeView: Extracted', referrals?.length, 'referral relationships');

      // Debug: Check if current user exists in users array
      const currentUserInArray = allUsers?.find((u: { id: number }) => u.id === userId);
      console.log('🌳 DownlineTreeView: Current user found in array:', !!currentUserInArray, currentUserInArray?.username);

      // Debug: Check referrals for this user
      const userReferrals = referrals?.filter((r: { referrer_id: number; referred_id: number }) => r.referrer_id === userId);
      console.log('🌳 DownlineTreeView: User has', userReferrals?.length, 'direct referrals');

      // Create commission lookup map from actual earned commissions (not transferred shares)
      // CRITICAL FIX: Use Number() for all IDs to ensure consistent lookups
      const commissionMap = new Map<number, { usdt: number; shares: number }>();
      if (commissionTransactions) {
        // Group transactions by referrer_id and sum up actual earned commissions
        (commissionTransactions as any[]).forEach((transaction: any) => {
          const referrerId = Number(transaction.referrer_id);
          const usdtCommission = parseFloat(transaction.usdt_commission || 0);
          const shareCommission = parseFloat(transaction.share_commission || 0);

          if (!commissionMap.has(referrerId)) {
            commissionMap.set(referrerId, { usdt: 0, shares: 0 });
          }

          const current = commissionMap.get(referrerId)!;
          current.usdt += usdtCommission;
          current.shares += shareCommission;
        });
      }

      // NEW: Create map of commission YOU earned FROM each specific person
      // Key: "referrer_id:referred_id" (composite key using numeric IDs)
      // Value: { usdt, shares } - commission YOU earned from THEIR purchases
      const commissionFromUserMap = new Map<string, { usdt: number; shares: number }>();

      // Add Pre Sale commissions (from commission_transactions)
      if (commissionTransactions) {
        commissionTransactions.forEach((transaction: any) => {
          const referredId = Number(transaction.referred_id); // Person who made the purchase
          const referrerId = Number(transaction.referrer_id); // Person who earned the commission
          const usdtCommission = parseFloat(transaction.usdt_commission || 0);
          const shareCommission = parseFloat(transaction.share_commission || 0);

          // Create a composite key: "referrer_id:referred_id"
          const key = `${referrerId}:${referredId}`;

          if (!commissionFromUserMap.has(key)) {
            commissionFromUserMap.set(key, { usdt: 0, shares: 0 });
          }

          const current = commissionFromUserMap.get(key)!;
          current.usdt += usdtCommission;
          current.shares += shareCommission;
        });
      }

      // Add Multi-Level commissions (Phase 2+) - ONLY Level 1
      if (multiLevelCommissions) {
        multiLevelCommissions.forEach((transaction: any) => {
          const referredId = Number(transaction.referred_id); // Person who made the purchase
          const referrerId = Number(transaction.referrer_id); // Person who earned the commission
          const usdtCommission = parseFloat(transaction.usdt_commission || 0);
          const shareCommission = parseFloat(transaction.shares_purchased || 0);

          // Create a composite key: "referrer_id:referred_id"
          const key = `${referrerId}:${referredId}`;

          if (!commissionFromUserMap.has(key)) {
            commissionFromUserMap.set(key, { usdt: 0, shares: 0 });
          }

          const current = commissionFromUserMap.get(key)!;
          current.usdt += usdtCommission;
          current.shares += shareCommission;
        });
      }

      // Create share holdings lookup map
      // CRITICAL FIX: Use user_share_balances view as single source of truth to avoid double-counting
      // The old approach used user_share_holdings + commission_balances.share_balance which DOUBLE-COUNTED
      // multi-level commission shares (they were in BOTH aureus_share_purchases AND commission_balances)
      const holdingsMap = new Map<number, { purchased_shares: number; total_purchased: number; bonus_shares: number; transfers_received: number; transfers_sent: number; net_shares: number }>();

      // CRITICAL FIX: Use user_share_balances as the authoritative source for share calculations
      // This view correctly calculates: direct_purchases + commission_level_purchases + presale_commissions + converted + legacy - sent + received
      if (shareBalances) {
        shareBalances.forEach((balance: any) => {
          const userId = Number(balance.user_id);
          // Get total_invested from shareHoldings for display purposes
          const holdingData = shareHoldings?.find((h: any) => Number(h.user_id) === userId);
          const totalInvested = holdingData ? parseFloat(holdingData.total_invested || 0) : 0;

          holdingsMap.set(userId, {
            purchased_shares: parseFloat(balance.purchased_shares || 0),
            total_purchased: totalInvested,
            bonus_shares: parseFloat(balance.commission_bonus_shares || 0) + parseFloat(balance.converted_shares || 0) + parseFloat(balance.legacy_shares || 0),
            transfers_received: parseFloat(balance.shares_received || 0),
            transfers_sent: parseFloat(balance.shares_sent || 0),
            net_shares: parseFloat(balance.net_shares || 0)
          });
        });
      }

      // Fallback: Add any users from shareHoldings not in shareBalances (shouldn't happen but defensive)
      if (shareHoldings) {
        shareHoldings.forEach((holding: any) => {
          const userId = Number(holding.user_id);
          if (!holdingsMap.has(userId)) {
            holdingsMap.set(userId, {
              purchased_shares: parseFloat(holding.total_shares || 0),
              total_purchased: parseFloat(holding.total_invested || 0),
              bonus_shares: 0,
              transfers_received: 0,
              transfers_sent: 0,
              net_shares: parseFloat(holding.total_shares || 0)
            });
          }
        });
      }

      // NOTE: commission_conversions are already included in user_share_balances.converted_shares
      // We still query them separately above for the shareholdersSet categorization
      // ALSO: Add fallback for users with conversions who might not be in user_share_balances (edge case)
      if (commissionConversions) {
        commissionConversions.forEach((conversion: any) => {
          const userId = Number(conversion.user_id);
          const sharesRequested = parseFloat(conversion.shares_requested || 0);
          if (sharesRequested > 0 && !holdingsMap.has(userId)) {
            // User has conversions but not in user_share_balances - add them
            holdingsMap.set(userId, {
              purchased_shares: 0,
              total_purchased: 0,
              bonus_shares: sharesRequested,
              transfers_received: 0,
              transfers_sent: 0,
              net_shares: sharesRequested
            });
          }
        });
      }

      // Create referral count map
      const referralCountMap = new Map<number, number>();
      if (referrals) {
        referrals.forEach((referral: any) => {
          const referrerId = Number(referral.referrer_id);
          const count = referralCountMap.get(referrerId) || 0;
          referralCountMap.set(referrerId, count + 1);
        });
      }

      // Create user categorization maps
      const newShareholdersSet = new Set<number>();
      const activeEarnersSet = new Set<number>();
      const newTeamLeadersSet = new Set<number>();

      // Categorize shareholders (users with ANY shares from: purchases, bonuses, conversions, or transfers)
      // 1. Users with purchased shares (from aureus_share_purchases via user_share_holdings view)
      if (shareHoldings) {
        shareHoldings.forEach((holding: any) => {
          if (parseFloat(holding.total_shares || 0) > 0) {
            newShareholdersSet.add(Number(holding.user_id));
          }
        });
      }

      // 2. Users with commission share balance (from commission_balances.share_balance)
      // This includes both earned commission shares AND transferred shares
      if (commissionBalances) {
        commissionBalances.forEach((balance: any) => {
          if (parseFloat(balance.share_balance || 0) > 0) {
            newShareholdersSet.add(Number(balance.user_id));
          }
        });
      }

      // 3. Users with any holdings in holdingsMap (purchased_shares from direct purchases)
      holdingsMap.forEach((holdings, userId) => {
        if (holdings.purchased_shares > 0 || holdings.bonus_shares > 0) {
          newShareholdersSet.add(userId);
        }
      });

      // 4. Users who converted USDT commission to shares (from commission_conversions table)
      // These users should also be marked as "Active Shareholders"
      if (commissionConversions) {
        commissionConversions.forEach((conversion: any) => {
          if (parseFloat(conversion.shares_requested || 0) > 0) {
            newShareholdersSet.add(Number(conversion.user_id));
          }
        });
      }

      console.log('🌳 DownlineTreeView: Shareholders set has', newShareholdersSet.size, 'users (includes purchases, bonuses, conversions, transfers)');

      // Categorize active earners (ALL users with commission earnings across entire system)
      if (allCommissionTransactions) {
        allCommissionTransactions.forEach((transaction: any) => {
          activeEarnersSet.add(Number(transaction.referrer_id));
        });
      }

      // Categorize team leaders (users with their own referrals)
      if (referrals) {
        referrals.forEach((referral: any) => {
          newTeamLeadersSet.add(Number(referral.referrer_id));
        });
      }

      // Store sets in state for filtering
      setShareholdersSet(newShareholdersSet);
      setTeamLeadersSet(newTeamLeadersSet);

      // Build the tree structure with commission and holdings data
      const tree = buildTree(allUsers || [], referrals || [], userId, 0, commissionMap, referralCountMap, holdingsMap, newShareholdersSet, activeEarnersSet, commissionFromUserMap, Number(userId), newTeamLeadersSet);
      setTreeData(tree);
      setAllTreeData(tree); // Store unfiltered tree

      // Calculate statistics
      const stats = calculateTreeStats(tree, activeEarnersSet, newShareholdersSet, newTeamLeadersSet);
      setTreeStats(stats);

    } catch (error) {
      console.error('Error loading downline data:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildTree = (
    users: any[],
    referrals: any[],
    rootId: number,
    level: number,
    commissionMap: Map<number, any>,
    referralCountMap: Map<number, number>,
    holdingsMap: Map<number, any>,
    shareholdersSet: Set<number>,
    activeEarnersSet: Set<number>,
    commissionFromUserMap: Map<string, any>,
    viewingUserId: number,
    teamLeadersSet: Set<number>,
    visited: Set<number> = new Set()
  ): TreeNode | null => {
    // CRITICAL FIX: Ensure type consistency for ID comparisons
    const numericRootId = Number(rootId);

    // SAFETY: Prevent infinite recursion from circular referral data
    if (visited.has(numericRootId)) {
      console.warn('🌳 DownlineTreeView: Circular reference detected for user', numericRootId, '- skipping');
      return null;
    }
    visited.add(numericRootId);

    const user = users.find(u => Number(u.id) === numericRootId);
    if (!user || level > maxDepth) return null;

    // Find direct referrals - ensure numeric comparison
    const directReferrals = referrals
      .filter(r => Number(r.referrer_id) === numericRootId)
      .map(r => Number(r.referred_id));

    // Build children recursively - no filtering here, we'll filter in the UI based on active tab
    const children = directReferrals
      .map(childId => buildTree(users, referrals, childId, level + 1, commissionMap, referralCountMap, holdingsMap, shareholdersSet, activeEarnersSet, commissionFromUserMap, viewingUserId, teamLeadersSet, visited))
      .filter(child => child !== null) as TreeNode[];

    const commissionData = commissionMap.get(Number(user.id)) || { usdt: 0, shares: 0 };
    const holdingsData = holdingsMap.get(Number(user.id)) || { purchased_shares: 0, total_purchased: 0, bonus_shares: 0, transfers_received: 0, transfers_sent: 0 };

    // Get commission that the PARENT earned FROM this user
    let commissionFromThisUser = { usdt: 0, shares: 0 };
    const numericUserId = Number(user.id);
    if (level > 0) {
      const referral = referrals.find(r => Number(r.referred_id) === numericUserId);
      if (referral) {
        const key = `${Number(referral.referrer_id)}:${numericUserId}`;
        commissionFromThisUser = commissionFromUserMap.get(key) || { usdt: 0, shares: 0 };
      }
    }

    // Determine user status
    const hasPurchases = shareholdersSet.has(numericUserId);
    const isTeamLeader = teamLeadersSet.has(numericUserId);

    // Calculate NET transferred shares (received - sent)
    const netTransferredShares = holdingsData.transfers_received - holdingsData.transfers_sent;

    // CRITICAL FIX: Use net_shares from user_share_balances view as the authoritative total
    // This avoids double-counting that occurred when using user_share_holdings + commission_balances.share_balance
    // Fallback to manual calculation only if net_shares is not available
    const totalShares = (holdingsData as any).net_shares !== undefined
      ? Math.max(0, (holdingsData as any).net_shares)
      : Math.max(0, holdingsData.purchased_shares + holdingsData.bonus_shares + netTransferredShares);

    return {
      id: numericUserId,
      username: user.username,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email,
      telegram_username: user.telegram_username,
      phone: user.phone,
      total_referrals: referralCountMap.get(numericUserId) || 0,
      usdt_earnings: commissionData.usdt,
      share_earnings: commissionData.shares,
      purchased_shares: holdingsData.purchased_shares,
      total_purchased: holdingsData.total_purchased,
      created_at: user.created_at,
      is_active: user.is_active,
      level,
      children,
      parent_id: level > 0 ? rootId : undefined,
      commission_from_this_user_usdt: commissionFromThisUser.usdt,
      commission_from_this_user_shares: commissionFromThisUser.shares,
      has_purchases: hasPurchases,
      is_team_leader: isTeamLeader,
      total_shares: totalShares,
      bonus_shares: holdingsData.bonus_shares,
      transferred_shares: netTransferredShares // Now shows NET (received - sent)
    };
  };

  const calculateTreeStats = (
    node: TreeNode | null,
    activeEarnersSet: Set<number>,
    _shareholdersSet: Set<number>,
    _teamLeadersSet: Set<number>
  ): TreeStats => {
    if (!node) return { totalNodes: 0, activeNodes: 0, totalLevels: 0, totalUsdtEarnings: 0, totalShareEarnings: 0, activeEarners: 0, pendingUsers: 0, teamLeaders: 0 };

    let totalNodes = 1;
    let activeNodes = node.has_purchases ? 1 : 0; // Active = users with purchases
    let totalLevels = node.level + 1;
    let totalUsdtEarnings = node.usdt_earnings || 0;
    let totalShareEarnings = node.share_earnings || 0;
    let activeEarners = activeEarnersSet.has(node.id) ? 1 : 0;
    let pendingUsers = !node.has_purchases && node.level > 0 ? 1 : 0; // Pending = no purchases (exclude root)
    let teamLeaders = node.is_team_leader ? 1 : 0;

    const processChildren = (children: TreeNode[]) => {
      children.forEach(child => {
        totalNodes++;
        if (child.has_purchases) activeNodes++;
        if (!child.has_purchases) pendingUsers++;
        if (child.is_team_leader) teamLeaders++;
        if (activeEarnersSet.has(child.id)) activeEarners++;
        totalLevels = Math.max(totalLevels, child.level + 1);
        processChildren(child.children);
      });
    };

    processChildren(node.children);

    return { totalNodes, activeNodes, totalLevels, totalUsdtEarnings, totalShareEarnings, activeEarners, pendingUsers, teamLeaders };
  };

  const toggleNode = (nodeId: number) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const contactUser = (node: TreeNode) => {
    if (node.telegram_username) {
      window.open(`https://t.me/${node.telegram_username}`);
    } else {
      alert('No contact information available for this user.');
    }
  };

  const openMessageModal = (node: TreeNode) => {
    setMessageRecipient(node);
    setMessageModalOpen(true);
  };

  // v4.3.79: Privacy-aware helpers for the member details popup
  // Direct (Level 1) contacts get full visibility; deeper levels are masked to protect privacy
  const maskEmail = (email?: string | null): string => {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (!domain) return '•••';
    const visible = local.slice(0, Math.min(2, local.length));
    const dot = domain.lastIndexOf('.');
    const domName = dot > 0 ? domain.slice(0, dot) : domain;
    const tld = dot > 0 ? domain.slice(dot) : '';
    return `${visible}${'•'.repeat(Math.max(3, local.length - visible.length))}@${domName[0] || '•'}${'•'.repeat(2)}${tld}`;
  };

  const maskPhone = (phone?: string | null): string => {
    if (!phone) return '';
    const cleaned = phone.trim();
    if (cleaned.length <= 4) return '•••' + cleaned.slice(-2);
    return cleaned.slice(0, 3) + '•'.repeat(Math.max(3, cleaned.length - 5)) + cleaned.slice(-2);
  };

  const copyToClipboard = async (value: string, fieldKey: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        // Fallback for non-secure contexts
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(prev => (prev === fieldKey ? null : prev)), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // Normalize phone for wa.me / tel: URLs — strip spaces, dashes, parens; keep leading +
  const normalizePhoneForLink = (phone?: string | null): string => {
    if (!phone) return '';
    const trimmed = phone.trim();
    const hasPlus = trimmed.startsWith('+');
    const digits = trimmed.replace(/[^0-9]/g, '');
    return hasPlus ? `+${digits}` : digits;
  };

  // Filter tree based on active tab
  const filterTreeForTab = (node: TreeNode | null, tab: TabType): TreeNode | null => {
    if (!node) return null;

    // For root node (level 0), always include but filter children
    const isRoot = node.level === 0;

    // Does THIS node match the active tab?
    const selfMatches = isRoot || (
      tab === 'all' ? true :
      tab === 'active' ? node.has_purchases :
      tab === 'pending' ? !node.has_purchases :
      true
    );

    // Filter children recursively FIRST so we can decide whether to keep this
    // node based on its descendants as well as itself.
    const filteredChildren = node.children
      .map(child => filterTreeForTab(child, tab))
      .filter(child => child !== null) as TreeNode[];

    // BUGFIX (v4.8.48): keep a node if it matches the tab OR if any descendant
    // survives the filter. Previously a non-matching node returned null and took
    // its ENTIRE subtree with it — so e.g. on the "Pending" tab a pending member
    // sitting under an ACTIVE upline (or vice-versa) was pruned and vanished,
    // even though the tab count still included it. Retaining ancestors of a
    // matching descendant preserves tree structure without changing which
    // members are counted.
    if (!selfMatches && filteredChildren.length === 0) return null;

    return {
      ...node,
      children: filteredChildren
    };
  };

  // Get filtered tree based on active tab
  const filteredTree = useMemo(() => {
    return filterTreeForTab(treeData, activeTab);
  }, [treeData, activeTab]);

  // Search filter function
  const searchInTree = (node: TreeNode | null, query: string): TreeNode | null => {
    if (!node || !query.trim()) return node;

    const lowerQuery = query.toLowerCase();
    const matchesSearch =
      node.username.toLowerCase().includes(lowerQuery) ||
      node.first_name.toLowerCase().includes(lowerQuery) ||
      node.last_name.toLowerCase().includes(lowerQuery) ||
      node.email?.toLowerCase().includes(lowerQuery);

    const filteredChildren = node.children
      .map(child => searchInTree(child, query))
      .filter(child => child !== null) as TreeNode[];

    // Include node if it matches or has matching children
    if (matchesSearch || filteredChildren.length > 0) {
      return {
        ...node,
        children: filteredChildren
      };
    }

    return null;
  };

  // Apply search to filtered tree
  const displayTree = useMemo(() => {
    return searchQuery.trim() ? searchInTree(filteredTree, searchQuery) : filteredTree;
  }, [filteredTree, searchQuery]);

  // Count nodes for each tab
  const tabCounts = useMemo(() => {
    if (!treeData) return { active: 0, pending: 0, all: 0 };

    let active = 0, pending = 0, all = 0;

    const countNodes = (node: TreeNode) => {
      if (node.level > 0) { // Exclude root from counts
        all++;
        if (node.has_purchases) active++;
        else pending++;
      }
      node.children.forEach(countNodes);
    };

    countNodes(treeData);
    return { active, pending, all };
  }, [treeData]);

  const renderTreeNode = (node: TreeNode, isLast: boolean = false, prefix: string = '') => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children.length > 0;
    const isSelected = selectedNode?.id === node.id;

    // Determine avatar color based on status
    const avatarColor = node.has_purchases
      ? 'bg-green-600 text-white'
      : 'bg-amber-600 text-white';

    return (
      <div key={node.id} className="select-none">
        {/* Node Row */}
        <div
          className={`flex flex-col sm:flex-row sm:items-center p-2 sm:p-3 rounded-lg cursor-pointer transition-colors ${
            isSelected ? 'bg-blue-600/20 border border-blue-500/30' : 'hover:bg-gray-700/50'
          }`}
          onClick={() => setSelectedNode(node)}
        >
          {/* Top row on mobile: Tree lines, avatar, name, and expand button */}
          <div className="flex items-center w-full sm:flex-1">
            {/* Tree Lines - Hidden on mobile for cleaner look */}
            <div className="hidden sm:flex items-center mr-3 text-gray-500">
              <span className="font-mono text-sm">{prefix}</span>
              <span className="font-mono text-sm">{isLast ? '└─' : '├─'}</span>
            </div>

            {/* User Avatar with status indicator */}
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0 ${avatarColor}`}>
              {node.first_name?.[0] || node.username[0].toUpperCase()}
            </div>

            {/* User Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                <span className="text-white font-medium text-sm sm:text-base truncate">
                  {node.first_name} {node.last_name}
                </span>
                {/* Status Badges */}
                {node.level > 0 && (
                  <>
                    {node.has_purchases ? (
                      <span className="text-sm bg-green-600/30 text-green-400 px-2 py-0.5 rounded-full flex-shrink-0 border border-green-500/30">
                        ✓ Active
                      </span>
                    ) : (
                      <span className="text-sm bg-amber-600/30 text-amber-400 px-2 py-0.5 rounded-full flex-shrink-0 border border-amber-500/30">
                        ⏳ Pending
                      </span>
                    )}
                    {node.is_team_leader && (
                      <span className="text-sm bg-purple-600/30 text-purple-400 px-2 py-0.5 rounded-full flex-shrink-0 border border-purple-500/30">
                        👥 Leader
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className="text-sm sm:text-sm text-white mb-1">
                @{node.username} • Level {node.level} {node.total_referrals > 0 && `• ${node.total_referrals} referrals`}
              </div>

              {/* Mobile: Stack info vertically */}
              <div className="block sm:hidden space-y-1">
                {node.level === 1 && node.has_purchases && (
                  <div className="text-sm text-green-400 font-semibold">
                    💰 You earned: ${(node.commission_from_this_user_usdt || 0).toFixed(2)} USDT + {(node.commission_from_this_user_shares || 0).toFixed(2)} shares
                  </div>
                )}
                {node.has_purchases && (
                  <div className="text-sm text-yellow-400 font-medium">
                    🏆 {(node.total_shares || 0).toFixed(2)} total shares
                  </div>
                )}
                {!node.has_purchases && node.level > 0 && (
                  <div className="text-sm text-amber-400">
                    No shares yet - follow up recommended
                  </div>
                )}
              </div>

              {/* Desktop: Single line info */}
              {node.level === 1 && node.has_purchases && (
                <div className="hidden sm:block text-sm text-green-400 font-semibold mb-1">
                  💰 You earned: ${(node.commission_from_this_user_usdt || 0).toFixed(2)} USDT + {(node.commission_from_this_user_shares || 0).toFixed(2)} shares
                </div>
              )}
              {node.has_purchases && (
                <div className="hidden sm:block text-sm text-yellow-400 font-medium">
                  🏆 {(node.total_shares || 0).toFixed(2)} total shares
                </div>
              )}
              {!node.has_purchases && node.level > 0 && (
                <div className="hidden sm:block text-sm text-amber-400">
                  ⚠️ No shares yet - follow up recommended
                </div>
              )}
            </div>

            {/* Expand/Collapse Button */}
            {hasChildren && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNode(node.id);
                }}
                className="ml-2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-full flex-shrink-0"
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            )}
          </div>

          {/* Actions - Bottom row on mobile, right side on desktop */}
          <div className="flex items-center justify-between mt-2 sm:mt-0 sm:ml-4 sm:flex-shrink-0">
            <div className="flex items-center space-x-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openMessageModal(node);
                }}
                className="text-green-400 hover:text-green-300 text-lg sm:text-base p-1 sm:p-0"
                title="Send internal message"
              >
                💬
              </button>
              {node.telegram_username && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    contactUser(node);
                  }}
                  className="text-blue-400 hover:text-blue-300 text-lg sm:text-base p-1 sm:p-0"
                  title="Contact via Telegram"
                >
                  ✈️
                </button>
              )}
            </div>
            <span className="text-sm text-gray-500 ml-2">
              {new Date(node.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="ml-4 sm:ml-6 border-l border-gray-700 pl-2 sm:pl-4 mt-2">
            {node.children.map((child, index) => {
              const isLastChild = index === node.children.length - 1;
              const childPrefix = prefix + (isLast ? '  ' : '│ ');
              return renderTreeNode(child, isLastChild, childPrefix);
            })}
          </div>
        )}
      </div>
    );
  };



  if (loading) {
    return (
      <div className={`${className} text-center p-8`}>
        <div className="text-2xl mb-4">🌳</div>
        <div className="text-white">Loading your downline...</div>
      </div>
    );
  }

  if (!treeData) {
    return (
      <div className={`${className} text-center p-8`}>
        <div className="text-4xl mb-4">🌱</div>
        <h3 className="text-white text-xl mb-4">No Referrals Yet</h3>
        <p className="text-white mb-6">
          Start referring friends by sharing your referral link!
        </p>
      </div>
    );
  }



  return (
    <div className={className}>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-white text-xl sm:text-2xl font-bold">🌳 Your Referrals</h2>
        <p className="text-white text-sm sm:text-base">Manage and view your direct referrals</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap border-b border-gray-700 mb-4 gap-1">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'active'
              ? 'bg-green-600/20 text-green-400 border-b-2 border-green-500'
              : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
          }`}
        >
          ✓ Active ({tabCounts.active})
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'pending'
              ? 'bg-amber-600/20 text-amber-400 border-b-2 border-amber-500'
              : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
          }`}
        >
          ⏳ Pending ({tabCounts.pending})
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'all'
              ? 'bg-blue-600/20 text-blue-400 border-b-2 border-blue-500'
              : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
          }`}
        >
          👥 All ({tabCounts.all})
        </button>
      </div>

      {/* Tab Description */}
      <div className="mb-4 p-3 rounded-lg bg-gray-800/50 border border-gray-700">
        {activeTab === 'active' && (
          <p className="text-sm text-green-400">
            <strong>Active Members:</strong> Users who have purchased shares. These are your earning referrals!
          </p>
        )}
        {activeTab === 'pending' && (
          <p className="text-sm text-amber-400">
            <strong>Pending Referrals:</strong> Users who registered but haven't purchased shares yet. Follow up with them to convert!
          </p>
        )}
        {activeTab === 'all' && (
          <p className="text-sm text-blue-400">
            <strong>All Team Members:</strong> Complete view of your entire referral network.
          </p>
        )}
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by name, username, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 pl-10 text-white text-sm focus:outline-none focus:border-blue-500"
          />
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white">🔍</span>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Controls Row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-300 font-medium">Depth:</label>
          <select
            value={maxDepth}
            onChange={(e) => setMaxDepth(Number(e.target.value))}
            className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-1.5 text-white text-sm"
          >
            <option value={2}>2 Levels</option>
            <option value={3}>3 Levels</option>
            <option value={5}>5 Levels</option>
            <option value={10}>All Levels</option>
          </select>
        </div>
      </div>

      {/* Statistics */}
      {treeStats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4 text-center border border-gray-700">
            <div className="text-xl sm:text-2xl font-bold text-blue-400">{treeStats.totalNodes - 1}</div>
            <div className="text-sm sm:text-sm text-white">Total Team</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4 text-center border border-gray-700">
            <div className="text-xl sm:text-2xl font-bold text-green-400">{treeStats.activeNodes}</div>
            <div className="text-sm sm:text-sm text-white">Active</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4 text-center border border-gray-700">
            <div className="text-xl sm:text-2xl font-bold text-amber-400">{treeStats.pendingUsers}</div>
            <div className="text-sm sm:text-sm text-white">Pending</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4 text-center border border-gray-700">
            <div className="text-xl sm:text-2xl font-bold text-purple-400">{treeStats.teamLeaders}</div>
            <div className="text-sm sm:text-sm text-white">Team Leaders</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4 text-center border border-gray-700 col-span-2 lg:col-span-1">
            <div className="text-sm font-bold text-green-400">${(treeStats.totalUsdtEarnings || 0).toFixed(2)}</div>
            <div className="text-sm font-bold text-yellow-400">{(treeStats.totalShareEarnings || 0).toFixed(2)} shares</div>
            <div className="text-sm text-white">Your Earnings</div>
          </div>
        </div>
      )}

      {/* Tree View */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-3 sm:p-6 overflow-x-auto">
        {displayTree ? (
          <div className="space-y-2 min-w-0">
            {renderTreeNode(displayTree, true)}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">
              {activeTab === 'active' ? '✓' : activeTab === 'pending' ? '⏳' : '👥'}
            </div>
            <p className="text-white">
              {searchQuery
                ? `No results found for "${searchQuery}"`
                : activeTab === 'active'
                  ? 'No active members with purchases yet'
                  : activeTab === 'pending'
                    ? 'No pending referrals - great job converting!'
                    : 'No team members yet'
              }
            </p>
          </div>
        )}
      </div>

      {/* Member Details Popup Modal */}
      {selectedNode && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedNode(null)}
        >
          <div
            className="bg-gray-800 rounded-xl border border-gray-600 shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700 sticky top-0 bg-gray-800 rounded-t-xl z-10">
              <div className="flex items-center gap-3 min-w-0">
                <div className="relative flex-shrink-0">
                  <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center text-xl font-bold text-black shadow-md">
                    {selectedNode.first_name?.[0]?.toUpperCase() || selectedNode.username?.[0]?.toUpperCase() || '?'}
                  </div>
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-gray-800 ${
                      selectedNode.is_active ? 'bg-green-500' : 'bg-gray-500'
                    }`}
                    title={selectedNode.is_active ? 'Active' : 'Inactive'}
                  />
                </div>
                <div className="min-w-0">
                  {/* v4.3.80: Display full name prominently; fallback to @username if names are missing */}
                  <h3 className="text-white text-lg font-bold truncate">
                    {(selectedNode.first_name && selectedNode.last_name)
                      ? `${selectedNode.first_name} ${selectedNode.last_name}`
                      : `@${selectedNode.username}`}
                  </h3>
                  <p className="text-gray-300 text-sm truncate">@{selectedNode.username}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedNode(null)}
                className="text-gray-400 hover:text-white text-2xl w-10 h-10 flex items-center justify-center hover:bg-gray-700 rounded-full transition-colors flex-shrink-0 ml-2"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Status Badges */}
              <div className="flex flex-wrap gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedNode.has_purchases
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                }`}>
                  {selectedNode.has_purchases ? '✓ Active Shareholder' : '⏳ Pending'}
                </span>
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                  Level {selectedNode.level}
                </span>
                {selectedNode.total_referrals > 0 && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
                    👥 {selectedNode.total_referrals} Referrals
                  </span>
                )}
              </div>

              {/* Total Shares - Prominent Display */}
              <div className="bg-gradient-to-r from-yellow-900/30 to-amber-900/30 border border-yellow-500/30 rounded-lg p-4">
                <label className="text-sm text-yellow-400 font-semibold">🏆 Total Shares Owned</label>
                <div className="text-yellow-400 font-bold text-2xl mt-1">
                  {(selectedNode.total_shares || 0).toFixed(2)} shares
                </div>
                {(selectedNode.purchased_shares > 0 || selectedNode.bonus_shares > 0 || selectedNode.transferred_shares > 0) && (
                  <div className="text-sm text-white mt-2 space-y-1">
                    {selectedNode.purchased_shares > 0 && (
                      <div>💰 Purchased: {selectedNode.purchased_shares.toFixed(2)} shares (${(selectedNode.total_purchased || 0).toFixed(2)})</div>
                    )}
                    {selectedNode.bonus_shares > 0 && (
                      <div>🎁 Commission Balance: {selectedNode.bonus_shares.toFixed(2)} shares</div>
                    )}
                    {selectedNode.transferred_shares > 0 && (
                      <div>🔄 Converted: {selectedNode.transferred_shares.toFixed(2)} shares</div>
                    )}
                  </div>
                )}
              </div>

              {/* Commission YOU Earned - Only for Level 1 */}
              {selectedNode.level === 1 && (selectedNode.commission_from_this_user_usdt > 0 || selectedNode.commission_from_this_user_shares > 0) && (
                <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                  <label className="text-sm text-green-400 font-semibold">💰 Commission YOU Earned From This User</label>
                  <div className="text-green-400 font-bold text-xl mt-1">
                    ${(selectedNode.commission_from_this_user_usdt || 0).toFixed(2)} USDT + {(selectedNode.commission_from_this_user_shares || 0).toFixed(2)} shares
                  </div>
                </div>
              )}

              {/* Their Earnings */}
              {(selectedNode.usdt_earnings > 0 || selectedNode.share_earnings > 0) && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <label className="text-sm text-white font-semibold">📊 Their Commission Earnings (From Their Referrals)</label>
                  <div className="text-white font-medium mt-1">
                    ${(selectedNode.usdt_earnings || 0).toFixed(2)} USDT + {(selectedNode.share_earnings || 0).toFixed(2)} shares
                  </div>
                </div>
              )}

              {/* Quick Info Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-700/30 rounded-lg p-3">
                  <label className="text-sm text-white">Joined</label>
                  <div className="text-white text-sm font-medium">
                    {new Date(selectedNode.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="bg-gray-700/30 rounded-lg p-3">
                  <label className="text-sm text-white">Account Status</label>
                  <div className={`text-sm font-medium ${selectedNode.is_active ? 'text-green-400' : 'text-red-400'}`}>
                    {selectedNode.is_active ? '✓ Active' : '✕ Inactive'}
                  </div>
                </div>
              </div>

              {/* Contact Methods Panel — v4.3.79 */}
              {/* Privacy: full contact only visible for Level 1 (direct) referrals.
                  Deeper levels see masked previews; use in-platform Send Message instead. */}
              {(() => {
                const isDirect = selectedNode.level === 1;
                const hasEmail = !!selectedNode.email;
                const hasPhone = !!selectedNode.phone;
                const hasTelegram = !!selectedNode.telegram_username;
                return (
                  <div className="bg-gray-700/40 border border-gray-600/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                        <span>📇</span>
                        <span>Contact Methods</span>
                      </h4>
                      {!isDirect && (
                        <span
                          className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded-full"
                          title="Full contact details are only visible for your direct (Level 1) referrals"
                        >
                          🔒 Privacy protected
                        </span>
                      )}
                    </div>

                    {!hasEmail && !hasPhone && !hasTelegram && (
                      <div className="text-sm text-gray-400 italic">
                        No contact details available for this member.
                      </div>
                    )}

                    {/* Email row */}
                    {hasEmail && (
                      <div className="flex items-center gap-2 py-2 border-b border-gray-600/40 last:border-b-0">
                        <div className="w-9 h-9 rounded-lg bg-blue-500/20 text-blue-300 flex items-center justify-center flex-shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-gray-400 uppercase tracking-wide">Email</div>
                          <div className="text-sm text-white font-medium truncate" title={isDirect ? selectedNode.email : 'Hidden for privacy'}>
                            {isDirect ? selectedNode.email : maskEmail(selectedNode.email)}
                          </div>
                        </div>
                        {isDirect && (
                          <>
                            <button
                              onClick={() => copyToClipboard(selectedNode.email, 'email')}
                              className="p-2 rounded-md bg-gray-600/50 hover:bg-gray-500/70 text-gray-200 hover:text-white transition-colors"
                              title="Copy email"
                              aria-label="Copy email"
                            >
                              {copiedField === 'email' ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                              )}
                            </button>
                            <a
                              href={`mailto:${selectedNode.email}`}
                              className="p-2 rounded-md bg-blue-600/70 hover:bg-blue-500 text-white transition-colors"
                              title="Send email"
                              aria-label="Send email"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
                            </a>
                          </>
                        )}
                      </div>
                    )}

                    {/* Phone / WhatsApp row */}
                    {hasPhone && (
                      <div className="flex items-center gap-2 py-2 border-b border-gray-600/40 last:border-b-0">
                        <div className="w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center flex-shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2.28a2 2 0 011.94 1.515l.7 2.8a2 2 0 01-.45 1.95l-1.27 1.27a11 11 0 005.58 5.58l1.27-1.27a2 2 0 011.95-.45l2.8.7A2 2 0 0121 16.72V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-gray-400 uppercase tracking-wide">Phone / WhatsApp</div>
                          <div className="text-sm text-white font-medium truncate" title={isDirect ? selectedNode.phone : 'Hidden for privacy'}>
                            {isDirect ? selectedNode.phone : maskPhone(selectedNode.phone)}
                          </div>
                        </div>
                        {isDirect && (
                          <>
                            <button
                              onClick={() => copyToClipboard(selectedNode.phone || '', 'phone')}
                              className="p-2 rounded-md bg-gray-600/50 hover:bg-gray-500/70 text-gray-200 hover:text-white transition-colors"
                              title="Copy phone number"
                              aria-label="Copy phone number"
                            >
                              {copiedField === 'phone' ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                              )}
                            </button>
                            <a
                              href={`tel:${normalizePhoneForLink(selectedNode.phone)}`}
                              className="p-2 rounded-md bg-emerald-600/70 hover:bg-emerald-500 text-white transition-colors"
                              title="Call"
                              aria-label="Call"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2.28a2 2 0 011.94 1.515l.7 2.8a2 2 0 01-.45 1.95l-1.27 1.27a11 11 0 005.58 5.58l1.27-1.27a2 2 0 011.95-.45l2.8.7A2 2 0 0121 16.72V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                            </a>
                            <a
                              href={`https://wa.me/${normalizePhoneForLink(selectedNode.phone).replace(/^\+/, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded-md bg-green-600/80 hover:bg-green-500 text-white transition-colors"
                              title="Open WhatsApp"
                              aria-label="Open WhatsApp"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.031-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                            </a>
                          </>
                        )}
                      </div>
                    )}

                    {/* Telegram row — username is not sensitive; shown at any level */}
                    {hasTelegram && (
                      <div className="flex items-center gap-2 py-2 border-b border-gray-600/40 last:border-b-0">
                        <div className="w-9 h-9 rounded-lg bg-sky-500/20 text-sky-300 flex items-center justify-center flex-shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-gray-400 uppercase tracking-wide">Telegram</div>
                          <div className="text-sm text-white font-medium truncate">
                            @{selectedNode.telegram_username}
                          </div>
                        </div>
                        <button
                          onClick={() => copyToClipboard(`@${selectedNode.telegram_username}`, 'telegram')}
                          className="p-2 rounded-md bg-gray-600/50 hover:bg-gray-500/70 text-gray-200 hover:text-white transition-colors"
                          title="Copy Telegram handle"
                          aria-label="Copy Telegram handle"
                        >
                          {copiedField === 'telegram' ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                          )}
                        </button>
                        <button
                          onClick={() => contactUser(selectedNode)}
                          className="p-2 rounded-md bg-sky-600/80 hover:bg-sky-500 text-white transition-colors"
                          title="Contact via Telegram"
                          aria-label="Contact via Telegram"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Primary Action — always available via in-platform messaging */}
              <div className="pt-1">
                <button
                  onClick={() => {
                    openMessageModal(selectedNode);
                    setSelectedNode(null);
                  }}
                  className="w-full bg-green-600 hover:bg-green-500 text-white px-4 py-3 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 shadow-md"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                  Send Internal Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Message Modal */}
      {messageRecipient && (
        <SendMessageModal
          isOpen={messageModalOpen}
          onClose={() => {
            setMessageModalOpen(false);
            setMessageRecipient(null);
          }}
          recipientId={messageRecipient.id}
          recipientName={`${messageRecipient.first_name} ${messageRecipient.last_name} (@${messageRecipient.username})`}
          senderId={userId}
        />
      )}
    </div>
  );
};
